import React, { useEffect, useRef } from "react";
import '../App.css'
export default function ParticlesCanvas({ enabled = true }) {
  const canvasRef = useRef(null);
  const rafIdRef = useRef(0);
  const stateRef = useRef({ enabled });

  useEffect(() => {
    stateRef.current.enabled = enabled;
  }, [enabled]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!(canvas instanceof HTMLCanvasElement)) return;

    const ctx = canvas.getContext("2d");

    let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    let width = 0;
    let height = 0;
    let particles = [];
    const mouse = { x: -9999, y: -9999 };

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) stateRef.current.enabled = false;

    const rand = (min, max) => Math.random() * (max - min) + min;
    const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

    function readVar(name, fallback) {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
      return v || fallback;
    }

    function seed() {
      const area = width * height;
      const density = 1 / 12000;
      const target = clamp(Math.floor(area * density), 40, 160);

      particles = new Array(target).fill(0).map(() => ({
        x: rand(0, width),
        y: rand(0, height),
        vx: rand(-0.4, 0.4),
        vy: rand(-0.4, 0.4),
        r: rand(1.2, 2.2),
      }));
    }

    function resize() {
      // ⭐ FIX: set actual size
      width = window.innerWidth;
      height = window.innerHeight;

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);

      canvas.style.width = width + "px";
      canvas.style.height = height + "px";

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      seed();
    }

    function update() {
      const linkDist = 110;
      const repelRadius = 80;
      const repelStrength = 0.08;

      for (const p of particles) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;

        if (d2 < repelRadius * repelRadius) {
          const d = Math.sqrt(d2) || 1;
          const ux = dx / d,
            uy = dy / d;
          p.vx += ux * repelStrength;
          p.vy += uy * repelStrength;
        }

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;

        p.vx *= 0.997;
        p.vy *= 0.997;
      }

      ctx.clearRect(0, 0, width, height);

      const dot = readVar("--muted", "#88a");
      const line = readVar("--brand", "#59f");

      ctx.fillStyle = dot;

      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.strokeStyle = line;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkDist * linkDist) {
            const alpha = 1 - Math.sqrt(d2) / linkDist;
            ctx.globalAlpha = alpha * 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;
    }

    function loop() {
      if (!stateRef.current.enabled) return;
      update();
      rafIdRef.current = requestAnimationFrame(loop);
    }

    function onResize() {
      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      resize();
    }

    const onPointerMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    const onPointerLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerleave", onPointerLeave, { passive: true });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        cancelAnimationFrame(rafIdRef.current);
      } else if (stateRef.current.enabled) {
        loop();
      }
    });

    // ⭐ Start everything
    resize();
    if (stateRef.current.enabled) loop();

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerleave", onPointerLeave);
    };
  }, []);

  return <canvas id="particles" ref={canvasRef} aria-hidden="true"></canvas>;
}
